// excel_gen/field_sheet_parse — read field_sheet xlsx back to flat records
// inverse of field_sheet_to_sheet: reads key row (row 3) for column keys, data rows (row 5+) for values

import ExcelJS from 'exceljs'

// layout constants (must match field_sheet.ts)
const ROW_KEY_EXCEL = 3 // 1-indexed: row 3 contains column keys
const ROW_DATA_FIRST_EXCEL = 5 // 1-indexed: row 5 is first data row

// --

export type ParsedSheet = {
	name: string
	col_keys: string[] // column keys from key row, in order
	rows: Record<string, unknown>[] // flat records keyed by col_key
	tab_color?: string // hex color from sheet tab (without #)
}

/** parse a single sheet from an xlsx file */
export const field_sheet_parse = async (xlsx_path: string, sheet_name: string): Promise<ParsedSheet> => {
	const wb = new ExcelJS.Workbook()
	await wb.xlsx.readFile(xlsx_path)
	const ws = wb.getWorksheet(sheet_name)
	if (!ws) throw new Error(`sheet '${sheet_name}' not found in ${xlsx_path}`)
	return parse_worksheet(ws)
}

/** parse all sheets from an xlsx file */
export const field_sheet_parse_all = async (xlsx_path: string): Promise<ParsedSheet[]> => {
	const wb = new ExcelJS.Workbook()
	await wb.xlsx.readFile(xlsx_path)
	return field_sheet_parse_workbook(wb)
}

/** parse all sheets from an in-memory ExcelJS workbook (browser-compatible) */
export const field_sheet_parse_from_buffer = async (buffer: ArrayBuffer): Promise<ParsedSheet[]> => {
	const wb = new ExcelJS.Workbook()
	await wb.xlsx.load(buffer)
	return field_sheet_parse_workbook(wb)
}

/** parse all sheets from an ExcelJS workbook instance */
export const field_sheet_parse_workbook = (wb: ExcelJS.Workbook): ParsedSheet[] => {
	const results: ParsedSheet[] = []
	wb.eachSheet(ws => {
		results.push(parse_worksheet(ws))
	})
	return results
}

// --

const parse_worksheet = (ws: ExcelJS.Worksheet): ParsedSheet => {
	const name = ws.name

	// read key row — column keys
	const key_row = ws.getRow(ROW_KEY_EXCEL)
	const col_keys: string[] = []
	const col_indices: number[] = [] // 1-indexed column numbers with valid keys

	key_row.eachCell({includeEmpty: false}, (cell, col_num) => {
		const val = cell_value_str(cell)
		if (val && !val.startsWith('_sep')) {
			col_keys.push(val)
			col_indices.push(col_num)
		}
	})

	// read data rows
	const rows: Record<string, unknown>[] = []
	const row_count = ws.rowCount

	for (let r = ROW_DATA_FIRST_EXCEL; r <= row_count; r++) {
		const ws_row = ws.getRow(r)
		// skip empty rows (check if any cell has a value)
		let has_value = false
		const rec: Record<string, unknown> = {}

		for (let i = 0; i < col_keys.length; i++) {
			const key = col_keys[i]!
			const col_num = col_indices[i]!
			const cell = ws_row.getCell(col_num)
			const val = cell_value(cell)
			if (val !== null && val !== undefined && val !== '') has_value = true
			rec[key] = val
		}

		if (!has_value) continue // skip fully blank rows
		rows.push(rec)
	}

	// extract tab color if available
	const props = ws.properties as Record<string, unknown> | undefined
	const tc = props?.tabColor as {argb?: string} | undefined
	const tab_color = tc?.argb ? tc.argb.slice(2) : undefined // strip FF alpha prefix

	return {name, col_keys, rows, tab_color}
}

/** extract cell value as native type */
const cell_value = (cell: ExcelJS.Cell): unknown => {
	const v = cell.value
	if (v === null || v === undefined) return null

	// ExcelJS rich text
	if (typeof v === 'object' && 'richText' in v) {
		return (v as any).richText.map((rt: any) => rt.text).join('')
	}

	// ExcelJS formula result
	if (typeof v === 'object' && 'formula' in v) {
		return (v as any).result ?? null
	}

	// ExcelJS shared formula
	if (typeof v === 'object' && 'sharedFormula' in v) {
		return (v as any).result ?? null
	}

	// ExcelJS hyperlink
	if (typeof v === 'object' && 'hyperlink' in v) {
		return (v as any).text ?? (v as any).hyperlink ?? null
	}

	// ExcelJS error
	if (typeof v === 'object' && 'error' in v) {
		return null
	}

	// Date
	if (v instanceof Date) {
		// format as YYYY-MM-DD
		const y = v.getFullYear()
		const m = String(v.getMonth() + 1).padStart(2, '0')
		const d = String(v.getDate()).padStart(2, '0')
		return `${y}-${m}-${d}`
	}

	return v
}

/** extract cell value as string (for key row) */
const cell_value_str = (cell: ExcelJS.Cell): string | null => {
	const v = cell_value(cell)
	if (v === null || v === undefined) return null
	return String(v)
}
